import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  MessageCircle, Loader2, RefreshCw, Copy, CheckCircle2,
  Sparkles, Trash2, Search, Send, Clock, ArrowRight,
  BarChart3, Users, Edit3, Save, X, Globe, ShieldCheck,
  MessageSquareReply, AlertTriangle, Phone
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { sentAtLabel } from '../components/outbox/outboxUtils';

// ─── Stats Cards ─────────────────────────────────────────
function StatsBar({ stats }) {
  const items = [
    { label: 'Total Tasks', value: stats.total_tasks || 0, icon: MessageCircle, color: 'text-emerald-500' },
    { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'text-amber-500' },
    { label: 'Completed', value: stats.completed || 0, icon: CheckCircle2, color: 'text-emerald-500' },
    { label: 'Replies', value: stats.total_replies || 0, icon: MessageSquareReply, color: 'text-sky-500' },
    { label: 'Reply Rate', value: `${stats.reply_rate || 0}%`, icon: BarChart3, color: 'text-violet-500' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="whatsapp-stats">
      {items.map((item, i) => (
        <Card key={i} className="border-border/50">
          <CardContent className="p-3 flex items-center gap-3">
            <item.icon className={`h-5 w-5 ${item.color} shrink-0`} />
            <div>
              <p className="text-lg font-bold leading-none">{item.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Task Card ───────────────────────────────────────────
function TaskCard({ task, onDelete, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(task.message);
  const isCompleted = task.status === 'completed';

  const handleSave = async () => {
    try {
      await api.put(`/api/whatsapp-tasks/${task.id}/message`, { message: editText });
      onEdit(task.id, editText);
      setEditing(false);
      toast.success('Message updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const accentBadge = {
    us_english: { label: 'US', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    indian_english: { label: 'IN', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
    hinglish: { label: 'HI', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  };
  const ab = accentBadge[task.accent] || { label: task.market_code || '??', className: 'bg-gray-100 text-gray-700' };

  const statusColor = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    queued: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
    cancelled_opt_out: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
  };

  return (
    <Card className={`border-border/50 ${isCompleted ? 'opacity-75' : ''}`} data-testid={`wa-task-${task.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="h-4 w-4 text-emerald-500 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{task.lead_name}</p>
              <p className="text-xs text-muted-foreground truncate">{task.lead_company || 'N/A'} &middot; {task.phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge className={ab.className + ' text-[10px] px-1.5 py-0'}>{ab.label}</Badge>
            <Badge className={statusColor[task.status] + ' text-[10px] px-1.5 py-0'}>{task.status}</Badge>
          </div>
        </div>

        {editing ? (
          <div className="space-y-2">
            <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={3} className="text-sm" data-testid="wa-edit-textarea" />
            <div className="flex gap-2">
              <Button size="sm" variant="default" onClick={handleSave}><Save className="h-3 w-3 mr-1" />Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditText(task.message); }}><X className="h-3 w-3 mr-1" />Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm bg-muted/30 rounded-md p-2.5 leading-relaxed">{task.message}</p>
        )}

        {task.sequence_id && (
          <p className="text-xs text-muted-foreground">Step {task.sequence_step} of sequence &middot; {task.delay_days ? `Scheduled +${task.delay_days}d` : 'Immediate'}</p>
        )}

        {isCompleted && sentAtLabel(task.sent_at || task.executed_at || task.completed_at || task.updated_at) && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="wa-sent-time">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Sent {sentAtLabel(task.sent_at || task.executed_at || task.completed_at || task.updated_at)}
          </p>
        )}

        {!isCompleted && (
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(true); setEditText(task.message); }} data-testid="wa-edit-btn">
              <Edit3 className="h-3 w-3 mr-1" />Edit
            </Button>
            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => onDelete(task.id)} data-testid="wa-delete-btn">
              <Trash2 className="h-3 w-3 mr-1" />Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Reply Card ──────────────────────────────────────────
function ReplyCard({ reply }) {
  const analysis = reply.brain_analysis || {};
  const sentimentColor = {
    positive: 'text-emerald-600',
    neutral: 'text-gray-500',
    negative: 'text-red-500',
  };

  return (
    <Card className="border-border/50" data-testid={`wa-reply-${reply.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquareReply className="h-4 w-4 text-sky-500" />
            <p className="font-medium text-sm">{reply.contact_name || reply.phone || 'Unknown'}</p>
          </div>
          {analysis.sentiment && (
            <span className={`text-xs font-medium ${sentimentColor[analysis.sentiment] || 'text-gray-500'}`}>
              {analysis.sentiment}
            </span>
          )}
        </div>
        <p className="text-sm bg-muted/30 rounded-md p-2.5">{reply.message_text}</p>
        {analysis.intent && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px]">Intent: {analysis.intent}</Badge>
            {analysis.language && <Badge variant="outline" className="text-[10px]">Lang: {analysis.language}</Badge>}
            {analysis.suggested_action && <Badge variant="outline" className="text-[10px]">Action: {analysis.suggested_action}</Badge>}
          </div>
        )}
        {analysis.summary && <p className="text-xs text-muted-foreground italic">{analysis.summary}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────
export default function WhatsAppOutbox({ embedded = false }) {
  const [tasks, setTasks] = useState([]);
  const [replies, setReplies] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('tasks');
  const [generating, setGenerating] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [tasksRes, statsRes, repliesRes, leadsRes] = await Promise.all([
        api.get('/api/whatsapp-tasks/'),
        api.get('/api/whatsapp-tasks/stats'),
        api.get('/api/whatsapp/replies'),
        api.get('/api/leads/?limit=20'),
      ]);
      setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      setStats(statsRes.data || {});
      setReplies(repliesRes.data?.replies || []);
      const leadList = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.leads || []);
      setLeads(leadList);
      if (leadList.length > 0 && !selectedLeadId) setSelectedLeadId(leadList[0].id);
    } catch (e) {
      console.error('Failed to load WhatsApp data', e);
    } finally {
      setLoading(false);
    }
  }, [selectedLeadId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerate = async () => {
    if (!selectedLeadId) return;
    setGenerating(true);
    try {
      await api.post(`/api/whatsapp-tasks/${selectedLeadId}/generate`, { message_type: 'intro' });
      toast.success('WhatsApp task generated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await api.delete(`/api/whatsapp-tasks/${taskId}`);
      toast.success('Task cancelled');
      loadData();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const handleEdit = (taskId, newMessage) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, message: newMessage } : t));
  };

  const filteredTasks = tasks.filter(t =>
    !search || t.lead_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.message?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-outbox-page">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-emerald-500" />
              WhatsApp Outbox
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Brain-powered WhatsApp outreach with market-aware language &amp; tone
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
        </div>
      )}

      <StatsBar stats={stats} />

      {/* Generate New Task */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Select Lead</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedLeadId}
                onChange={e => setSelectedLeadId(e.target.value)}
                data-testid="wa-lead-select"
              >
                {leads.map(l => (
                  <option key={l.id} value={l.id}>{l.name} — {l.company || 'N/A'}</option>
                ))}
              </select>
            </div>
            <Button onClick={handleGenerate} disabled={generating || !selectedLeadId} data-testid="wa-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Generate Message
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="tasks" className="gap-2" data-testid="wa-tab-tasks">
            <Send className="h-4 w-4" />Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="replies" className="gap-2" data-testid="wa-tab-replies">
            <MessageSquareReply className="h-4 w-4" />Replies ({replies.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="wa-search-input"
              />
            </div>
          </div>

          {filteredTasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center" data-testid="whatsapp-outbox-empty">
                <MessageCircle className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm font-medium">No WhatsApp tasks yet</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                  WhatsApp cadence branch is on the P1 roadmap. Today you can generate single-task messages from a lead's detail page.
                </p>
                <Button size="sm" variant="outline" className="mt-4" onClick={() => window.location.href = '/leads'} data-testid="whatsapp-go-pipeline-btn">
                  Go to Pipeline
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredTasks.map(task => (
                <TaskCard key={task.id} task={task} onDelete={handleDelete} onEdit={handleEdit} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="replies" className="space-y-4 mt-4">
          {replies.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <MessageSquareReply className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No WhatsApp replies received yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {replies.map(reply => (
                <ReplyCard key={reply.id} reply={reply} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
